<script setup lang="ts">
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { ref, toRef } from "vue";
import AccountAction from "@/components/AccountAction.vue";
import AppButton from "@/components/AppButton.vue";
import AppInputText from "@/components/AppInputText.vue";
import AppLink from "@/components/AppLink.vue";
import useFormValidation from "@/modules/useFormValidation";

const auth = getAuth();

const {
    validateEmailField,
    errors,
    hasErrors
} = useFormValidation();

interface Props {
    username: string;
    message: string;
}

const props = defineProps<Props>();

defineEmits(["cancel"]);

const emailAddress = ref(props.username);
const emailSent = ref(false);

const validateEmailInput = () =>
    validateEmailField("email", "Email", emailAddress.value);

const handleSend = async () => {
    try {
        validateEmailInput();
        if (hasErrors.value) return;
        await sendPasswordResetEmail(auth, emailAddress.value);
        emailSent.value = true;
    } catch (error) {
        console.error(error);
    }
};
</script>

<template>
    <AccountAction>
        <template #title>
                {{ emailSent ? 'Check your email' : 'Recover password' }}
        </template>
        <template #default>
            <div class="flex flex-col gap-4">
                <p v-show="message" class="text-critical-text text-sm">
                    {{ message }}
                </p>

                <div v-if="emailSent">
                    <p class="text-normal-emphasis">
                        Follow the instructions sent to <span class="font-semibold">{{ emailAddress }}</span> to recover your password
                    </p>
                </div>
                <div v-else>
                    <p class="text-normal-emphasis mb-4">
                        Get instructions sent to this email that explain how to reset your password
                    </p>
                    <AppInputText
                        name="email"
                        label="Email"
                        type="email"
                        v-model="emailAddress"
                        :required="true"
                        @blur="validateEmailInput"
                        @keyup="validateEmailInput"
                    />
                </div>
            </div>
        </template>
        <template #actions>
            <div v-if="emailSent" class="flex justify-evenly items-center w-full">
                <AppButton class="w-full"
                    title="Done"
                    @click="$emit('cancel')"
                />
            </div>
            <div v-else class="flex flex-col  w-full">
                <AppButton class="w-full"
                    title="Send"
                    @click="handleSend"
                />
                <AppCancel @click="$emit('cancel')" />
            </div>
        </template>
    </AccountAction>
</template>
